import React from 'react';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from '@billda/design-system';

export interface ContextMenuItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  dividerAfter?: boolean;
}

export interface ContextMenuProps {
  open: boolean;
  onClose: () => void;
  anchorPosition?: { top: number; left: number };
  anchorEl?: HTMLElement | null;
  items: ContextMenuItem[];
}

/**
 * Controlled dropdown menu anchored to a position or an element.
 *
 * Renders through the DS Menu primitives: the anchor is a zero-size trigger
 * span pinned (fixed) to the anchor point, so the portalled MenuContent
 * positions against it exactly like the MUI anchorReference behaviour.
 */
const ContextMenu: React.FC<ContextMenuProps> = ({
  open,
  onClose,
  anchorPosition,
  anchorEl,
  items,
}) => {
  // Recomputed each render so an element anchor follows layout shifts.
  const anchorPoint = (() => {
    if (anchorPosition) {
      return anchorPosition;
    }
    if (anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      return { top: rect.bottom, left: rect.left };
    }
    return { top: 0, left: 0 };
  })();

  return (
    <Menu
      open={open}
      onOpenChange={next => {
        if (!next) {
          onClose();
        }
      }}
    >
      <MenuTrigger asChild>
        <span
          aria-hidden="true"
          tabIndex={-1}
          style={{
            position: 'fixed',
            top: anchorPoint.top,
            left: anchorPoint.left,
            width: 0,
            height: 0,
          }}
        />
      </MenuTrigger>
      <MenuContent data-testid="context-menu">
        {items.map(item => (
          <React.Fragment key={item.key}>
            <MenuItem
              onSelect={() => {
                item.onClick();
                onClose();
              }}
              disabled={item.disabled}
              data-testid={`context-menu-item-${item.key}`}
            >
              {item.icon}
              {item.label}
            </MenuItem>
            {item.dividerAfter && <MenuSeparator />}
          </React.Fragment>
        ))}
      </MenuContent>
    </Menu>
  );
};

export default ContextMenu;
